<template>
    <div class="privacy-notice">
      <h3 style="font-size: 15px; margin-bottom: 30px;">Last updated August 01, 2024</h3>
      <p>
        This privacy notice for PT Surya Mandiri Group ("we," "us," or "our"), describes how and why we might collect, store, use, and/or share ("process") your information when you use our services ("Services"), such as when you:
      </p>
      <ul>
        <li>Download and use our mobile application (IndoBeauty), or any other application of ours that links to this privacy notice</li>
        <li>Engage with us in other related ways, including any sales, marketing, or events</li>
      </ul>
      <p>
        Questions or concerns? Reading this privacy notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at <a href="mailto:notification@indobeauty.id">notification@indobeauty.id</a>.
      </p>
  
      <h2>SUMMARY OF KEY POINTS</h2>
      <p style="margin-bottom: 10px">This summary provides key points from our privacy notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.</p>
      <ul>
        <li><a href="#what-information">What personal information do we process?</a></li>
        <li><a href="#sensitive-information">Do we process any sensitive personal information?</a></li>
        <li><a href="#information-from-third-parties">Do we collect any information from third parties?</a></li>
        <li><a href="#how-we-process">How do we process your information?</a></li>
        <li><a href="#sharing-information">In what situations and with which parties do we share personal information?</a></li>
        <li><a href="#data-retention">How long do we keep your information?</a></li>
        <li><a href="#data-security">How do we keep your information safe?</a></li>
        <li><a href="#information-from-minors">Do we collect information from minors?</a></li>
        <li><a href="#privacy-rights">What are your privacy rights?</a></li>
        <li><a href="#do-not-track">Controls for Do-Not-Track features</a></li>
        <li><a href="#updates-to-notice">Do we make updates to this notice?</a></li>
        <li><a href="#contact-us">How can you contact us about this notice?</a></li>
        <li><a href="#review-update-delete">How can you review, update, or delete the data we collect from you?</a></li>
      </ul>
  
      <section id="what-information">
        <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
        <h3>Personal information you disclose to us</h3>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We collect personal information that you provide to us.</p>
        <p>We collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.</p>
        <p><strong>Personal Information Provided by You:</strong> The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:</p>
        <ul>
          <li>Names</li>
          <li>Phone numbers</li>
          <li>Email addresses</li>
          <li>Usernames</li>
          <li>Passwords</li>
          <li>Billing addresses</li>
        </ul>
        <p><strong>Sensitive Information:</strong> We do not process sensitive information.</p>
        <p><strong>Application Data:</strong> If you use our application(s), we also may collect the following information if you choose to provide us with access or permission:</p>
        <ul>
          <li><strong>Mobile Device Data:</strong> We automatically collect device information (such as your mobile device ID, model, and manufacturer), operating system, version information and system configuration information, device and application identification numbers, browser type and version, hardware model, Internet service provider and/or mobile carrier, and Internet Protocol (IP) address (or proxy server). If you are using our application(s), we may also collect information about the phone network associated with your mobile device, your mobile device’s operating system or platform, the type of mobile device you use, your mobile device’s unique device ID, and information about the features of our application(s) you accessed.</li>
          <li><strong>Push Notifications:</strong> We may request to send you push notifications regarding your account or certain features of the application(s). If you wish to opt out from receiving these types of communications, you may turn them off in your device's settings.</li>
        </ul>
      </section>
  
      <section id="sensitive-information">
        <h2>2. DO WE PROCESS ANY SENSITIVE PERSONAL INFORMATION?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We do not process sensitive personal information.</p>
      </section>
  
      <section id="information-from-third-parties">
        <h2>3. DO WE COLLECT ANY INFORMATION FROM THIRD PARTIES?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We may collect limited data from public databases, marketing partners, and other outside sources.</p>
        <p>In order to enhance our ability to provide relevant marketing, offers, and services to you and update our records, we may obtain information about you from other sources, such as public databases, joint marketing partners, affiliate programs, data providers, and from other third parties. This information includes mailing addresses, job titles, email addresses, phone numbers, intent data (or user behavior data), Internet Protocol (IP) addresses, social media profiles, social media URLs, and custom profiles, for purposes of targeted advertising and event promotion.</p>
      </section>
  
      <section id="how-we-process">
        <h2>4. HOW DO WE PROCESS YOUR INFORMATION?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.</p>
        <p>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>
        <ul>
          <li>To deliver and facilitate delivery of services to the user.</li>
          <li>To respond to user inquiries/offer support to users.</li>
          <li>To send administrative information to you.</li>
          <li>To fulfill and manage your orders.</li>
          <li>To enable user-to-user communications.</li>
          <li>To send you marketing and promotional communications.</li>
          <li>To evaluate and improve our Services, products, marketing, and your experience.</li>
          <li>To identify usage trends.</li>
          <li>To determine the effectiveness of our marketing and promotional campaigns.</li>
          <li>To comply with our legal obligations.</li>
        </ul>
      </section>
  
      <section id="sharing-information">
        <h2>5. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We may share information in specific situations described in this section and/or with the following third parties.</p>
        <p>We may need to share your personal information in the following situations:</p>
        <ul>
          <li><strong>Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
          <li><strong>Offer Wall:</strong> Our application(s) may display a third-party hosted "offer wall." Such an offer wall allows third-party advertisers to offer virtual currency, gifts, or other items to users in return for the acceptance and completion of an advertisement offer.</li>
        </ul>
      </section>
  
      <section id="data-retention">
        <h2>6. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this privacy notice unless otherwise required by law.</p>
        <p>We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements).</p>
        <p>When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.</p>
      </section>
  
      <section id="data-security">
        <h2>7. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We aim to protect your personal information through a system of organizational and technical security measures.</p>
        <p>We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.</p>
        <p>If you have any questions about the security of your personal information, you can contact us at <a href="mailto:notification@indobeauty.id">notification@indobeauty.id</a>.</p>
      </section>
  
      <section id="information-from-minors">
        <h2>8. DO WE COLLECT INFORMATION FROM MINORS?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We do not knowingly solicit data from or market to children under 13 years of age.</p>
        <p>We do not knowingly solicit data from or market to children under 13 years of age. If we learn that we have collected personal information from a child under age 13 without verification of parental consent, we will delete that information as quickly as possible.</p>
        <p>If you become aware of any data we may have collected from children under age 13, please contact us at <a href="mailto:notification@indobeauty.id">notification@indobeauty.id</a>.</p>
      </section>
  
      <section id="privacy-rights">
        <h2>9. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: You may review, change, or delete your personal information, and you may also have other rights depending on your location.</p>
        <p>In some regions, such as the European Economic Area (EEA), you have rights under applicable data protection laws. These may include the right to access, correct, update, or delete your personal information. You may also have the right to restrict or object to the processing of your personal information, or to obtain a copy of your personal information in a portable format.</p>
        <p>To exercise these rights, please contact us at <a href="mailto:notification@indobeauty.id">notification@indobeauty.id</a>.</p>
      </section>
  
      <section id="do-not-track">
        <h2>10. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: We do not respond to Do-Not-Track signals.</p>
        <p>Most web browsers and some mobile operating systems include a Do-Not-Track (“DNT”) feature or setting you can activate to signal your preference not to be tracked online. However, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked.</p>
      </section>
  
      <section id="updates-to-notice">
        <h2>11. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</p>
        <p>We may update this privacy notice from time to time in order to stay compliant with relevant laws or to reflect any changes to our practices. We encourage you to review this privacy notice periodically to stay informed of how we are protecting your information.</p>
      </section>
  
      <section id="contact-us">
        <h2>12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
        <p>If you have questions or comments about this notice, you may contact us at:</p>
        <ul>
          <li>Email: <a href="mailto:notification@indobeauty.id">notification@indobeauty.id</a></li>
          <li>Postal Address: PT Surya Mandiri Group, Duta Harapan Indah, Jakarta, Indonesia 14460, Indonesia</li>
        </ul>
      </section>
  
      <section id="review-update-delete">
        <h2>13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h2>
        <p style="font-style: italic; margin-bottom: 10px">In Short: You may review, update, or delete your data by contacting us.</p>
        <p>You may review, update, or delete your data by contacting us at <a href="mailto:notification@indobeauty.id">notification@indobeauty.id</a>. If you have any issues with data deletion, please let us know so we can assist you.</p>
      </section>
  
      <footer>
        <p>For any inquiries, contact us at <a href="mailto:notification@indobeauty.id">notification@indobeauty.id</a></p>
        <address>PT Surya Mandiri Group, Duta Harapan Indah, Jakarta, Indonesia 14460, Indonesia</address>
      </footer>
    </div>
  </template>
  
  <script>
  export default {
    name: 'App'
  }
  </script>
  
<style scoped>

a {
  color: #1a73e8;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.privacy-notice {
    
 max-height: 100% !important;
 max-width: 100% !important;
  padding: 20px;
  max-width: 800px;
  margin: auto;
  background: white !important;
}

h1, h2, h3 {
  color: #333;
}

ul {
  list-style: disc;
  margin-left: 20px;
}

ul li {
  margin-bottom: 10px;
}

footer {
  margin-top: 20px;
  border-top: 1px solid #ddd;
  padding-top: 10px;
}

footer address {
  font-style: normal;
  color: #555;
}

h2{
    font-family: Arial !important;
  font-size: 19px !important;
  color: #000000 !important;
  line-height: 1.5;
  font-weight: 600;
  margin-top: 30px;
  margin-bottom: 30px;
}

p{
    font-size: 14px !important;
    font-family: Arial !important;
}
</style>
  